/**
 * Month-by-month billed projection for a proposal — the proposal-side analogue
 * of Cinch's contract-value chart (`buildContractProjection`). Shows how MRR
 * plays out over the term as time-limited discounts roll off.
 *
 * Unlike `computeTotals` (which BLENDS a time-based discount's value across the
 * whole term for a single MRR number), this computes each month independently so
 * the line STEPS UP when a promo ends:
 *  - percentage / fixed / tiered → reduce every month for the life of the term,
 *  - time_based → full reduction for months 1..duration, then 0,
 *  - quantity_based → recurring grants reduce every month; one-time grants apply
 *    in month 1 only.
 * Tiered gates on the target line's quantity, same as the summary math.
 *
 * Proposals have no billing-effective date, so there's no prorated stub — the
 * term is simply M1..M`months`.
 */

import type { CatalogDiscount, RenewalTerm } from "./api";
import {
  grossMrrOf,
  lineMetricId,
  lineSubtotal,
  priceOf,
  qtyOf,
  type BundleRow,
  type CatalogIndex,
  type DiscountRow,
  type LineRow,
} from "./calc";

export interface ProjectionPoint {
  month: number; // 1-based; 0 for the prorated P1 stub
  label: string; // "M1" (or "P1" for the stub)
  billed: number;
  /** Calendar label ("Jul '26") when a billing-effective date is set, else undefined. */
  calendarLabel?: string;
  /** True for the day-prorated first-period stub. */
  isStub?: boolean;
}

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The calendar month `offset` months after an ISO `YYYY-MM-DD` start, e.g. "Jul '26". */
function calendarLabelFor(startIso: string, offset: number): string | undefined {
  const [y, m] = startIso.split("-").map(Number);
  if (!y || !m) return undefined;
  const d = new Date(y, m - 1 + offset, 1);
  return `${MONTH_ABBR[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
}

export interface ProjectionMarker {
  month: number;
  label: string;
  kind: "term" | "discount";
}

/** Renewal lifecycle inputs (mirror Cinch contracts) that shape the horizon. */
export interface RenewalConfig {
  termMonths: number;
  isAutoRenew: boolean;
  renewalTerm: RenewalTerm | null;
  renewalTermMonths: number | null;
  mtmUpliftPct: number | null;
  /** ISO `YYYY-MM-DD`; anchors the timeline's calendar month labels. */
  billingEffectiveFrom?: string | null;
}

export interface Projection {
  points: ProjectionPoint[];
  markers: ProjectionMarker[];
  baseMonthly: number;
  total: number;
  term: number;
  firstTermTotal: number;
  secondTermTotal: number;
  /** P1 stub (if any) + M1..M12 — the contract-aligned "first 12 months" value. */
  firstYearTotal: number;
  avgMonthly: number;
  /** True when a mid-month billing-effective date produced a prorated P1 stub. */
  hasStub: boolean;
}

const num = (v: number | string | null | undefined): number => Number(v) || 0;

/** How one discount row contributes to the monthly projection. */
interface Window {
  perMonth: number; // reduction applied each active month
  activeMonths: number | null; // null = life of the contract
  month1Extra: number; // additional one-time reduction in M1
  endsAt: number | null; // month after which `perMonth` stops (for a marker)
  name: string;
}

function discountWindow(
  idx: CatalogIndex,
  d: CatalogDiscount,
  row: DiscountRow,
  lines: LineRow[],
  grossMrr: number,
  months: number,
): Window {
  const targetLi = row.target_line_local_id
    ? lines.find((l) => l._id === row.target_line_local_id)
    : null;
  const targetMonthly = targetLi ? lineSubtotal(targetLi) : grossMrr;
  const value = num(d.value);
  const base: Window = { perMonth: 0, activeMonths: null, month1Extra: 0, endsAt: null, name: d.name };

  if (d.type === "percentage") {
    return { ...base, perMonth: (targetMonthly * value) / 100 };
  }
  if (d.type === "fixed") {
    return { ...base, perMonth: Math.min(targetMonthly, value) };
  }
  if (d.type === "tiered") {
    const qtyOk = !targetLi || qtyOf(targetLi) >= (d.min_quantity || 0);
    return { ...base, perMonth: qtyOk ? (targetMonthly * value) / 100 : 0 };
  }
  if (d.type === "time_based") {
    const duration = num(row.duration_override) || d.duration_periods || months;
    const ends = Math.min(duration, months);
    return {
      ...base,
      perMonth: (targetMonthly * value) / 100,
      activeMonths: ends,
      endsAt: ends < months ? ends : null,
    };
  }
  if (d.type === "quantity_based") {
    let perMonth = 0;
    let month1Extra = 0;
    for (const g of d.unit_grants || []) {
      const override = row.unit_overrides[g.price_metric ?? ""];
      const qty = override != null ? num(override) : num(g.quantity);
      const matchLi = targetLi
        ? lineMetricId(idx, targetLi) === g.price_metric
          ? targetLi
          : null
        : lines.find((l) => lineMetricId(idx, l) === g.price_metric);
      if (!matchLi) continue;
      const monthlyValue = priceOf(matchLi) * qty;
      if (g.recurrence === "one_time") month1Extra += monthlyValue;
      else perMonth += monthlyValue;
    }
    return { ...base, perMonth, month1Extra };
  }
  return base;
}

export function buildProjection(
  idx: CatalogIndex,
  lines: LineRow[],
  discountRows: DiscountRow[],
  renewal: RenewalConfig,
  bundles: BundleRow[] = [],
): Projection {
  const term = Math.max(1, Math.min(renewal.termMonths || 12, 120));
  // Bundle-aware so an overridden package price flows into the timeline (and the
  // "1st 12-month value") consistently with the MRR/ARR summary cards.
  const baseMonthly = grossMrrOf(lines, bundles);

  // The term it renews into: an explicit different term, else the same term.
  const secondCycle =
    renewal.renewalTerm === "DIFFERENT_TERM" && (renewal.renewalTermMonths ?? 0) > 0
      ? Math.min(renewal.renewalTermMonths as number, 120)
      : term;

  // Horizon: always try to render the first term AND the start of the next, so
  // the renewal boundary is visible. Non-renewing → a short $0 tail past term.
  let horizon: number;
  if (!renewal.isAutoRenew) horizon = term + Math.min(6, Math.max(3, Math.round(term / 4)));
  else if (renewal.renewalTerm === "MONTH_TO_MONTH") horizon = term + Math.min(12, Math.max(6, term));
  else horizon = term + secondCycle;
  horizon = Math.max(1, Math.min(horizon, 120));

  const windows = discountRows
    .map((row) => {
      const d = idx.discountById.get(row.discount_id);
      return d ? discountWindow(idx, d, row, lines, baseMonthly, term) : null;
    })
    .filter((w): w is Window => w !== null);

  const reductionAt = (m: number) =>
    windows.reduce(
      (s, w) =>
        s +
        (w.activeMonths == null || m <= w.activeMonths ? w.perMonth : 0) +
        (m === 1 ? w.month1Extra : 0),
      0,
    );

  // Monthly billed, honoring renewal: after the first term, a non-renewing deal
  // bills $0; a month-to-month one applies the uplift; same/different-term keeps
  // the base. Discounts are tracked from M1 and are not re-applied on renewal.
  const billedAt = (m: number) => {
    let base = baseMonthly;
    if (m > term) {
      if (!renewal.isAutoRenew) return 0;
      if (renewal.renewalTerm === "MONTH_TO_MONTH")
        base = baseMonthly * (1 + (renewal.mtmUpliftPct ?? 0) / 100);
    }
    return Math.max(0, base - reductionAt(m));
  };

  const startIso = renewal.billingEffectiveFrom || null;

  // P1 prorated stub: Cinch bills on the 1st, so a mid-month start bills a
  // day-prorated first period (effective date → month-end), sharing M1's
  // discounts but NOT consuming a discount-duration period. The full months
  // M1.. then bill on the following 1st. (Mirrors contract-value-chart.)
  let hasStub = false;
  let stubBilled = 0;
  if (startIso) {
    const [y, mo, d] = startIso.split("-").map(Number);
    if (y && mo && d && d !== 1) {
      hasStub = true;
      const daysInMonth = new Date(y, mo, 0).getDate();
      const stubFraction = (daysInMonth - d + 1) / daysInMonth;
      stubBilled = Math.max(0, (baseMonthly - reductionAt(1)) * stubFraction);
    }
  }
  // When a stub precedes them, full months bill one calendar month later.
  const fullOffset = hasStub ? 1 : 0;
  const round2 = (x: number) => Math.round(x * 100) / 100;

  const points: ProjectionPoint[] = [];
  if (hasStub) {
    points.push({
      month: 0,
      label: "P1",
      billed: round2(stubBilled),
      calendarLabel: startIso ? calendarLabelFor(startIso, 0) : undefined,
      isStub: true,
    });
  }
  for (let m = 1; m <= horizon; m++) {
    points.push({
      month: m,
      label: `M${m}`,
      billed: billedAt(m),
      calendarLabel: startIso ? calendarLabelFor(startIso, fullOffset + (m - 1)) : undefined,
    });
  }

  const markers: ProjectionMarker[] = windows
    .filter((w) => w.endsAt != null)
    .map((w) => ({ month: w.endsAt as number, label: `${w.name} ends`, kind: "discount" as const }));

  // Term / renewal boundaries — the first-term / second-term dividers.
  if (!renewal.isAutoRenew) {
    if (term < horizon) markers.push({ month: term, label: "Term ends", kind: "term" });
  } else if (renewal.renewalTerm === "MONTH_TO_MONTH") {
    markers.push({ month: term, label: "Month-to-month", kind: "term" });
  } else {
    let b = term;
    let n = 1;
    while (b < horizon) {
      markers.push({ month: b, label: `Renewal ${n}`, kind: "term" });
      b += secondCycle;
      n += 1;
    }
  }

  // Sum full months by month number (immune to the optional leading P1 column).
  const sumFull = (from: number, toInclusive: number) => {
    let s = 0;
    for (let m = Math.max(1, from); m <= Math.min(toInclusive, horizon); m++) s += billedAt(m);
    return s;
  };
  const fullSum = sumFull(1, horizon);

  return {
    points,
    markers,
    baseMonthly,
    total: stubBilled + fullSum,
    term,
    firstTermTotal: stubBilled + sumFull(1, term),
    secondTermTotal: sumFull(term + 1, term + secondCycle),
    firstYearTotal: stubBilled + sumFull(1, 12),
    avgMonthly: horizon > 0 ? fullSum / horizon : 0,
    hasStub,
  };
}
